import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { BarChart3, TrendingUp, ShoppingBag, Users, Package, Search, DollarSign, Crown, CreditCard, Clock, CheckCircle } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar, CartesianGrid,
} from 'recharts';
import SEOHead from '../components/SEOHead';

const PIE_COLORS = ['#d97706', '#0f172a', '#e11d48', '#059669', '#7c3aed', '#0284c7', '#ca8a04', '#6366f1'];

const formatEur = (v) => `\u20AC${(v || 0).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StatCard = ({ icon: Icon, title, value, sub, accent }) => (
  <div className="bg-white border border-border/40 rounded-2xl p-5 hover:border-amber-200/50 hover:shadow-lg hover:shadow-amber-50/50 transition-all duration-300" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent || 'bg-amber-50'}`}>
        <Icon size={18} className="text-amber-600" />
      </div>
      <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{title}</span>
    </div>
    <p className="font-serif text-2xl text-obsidian mb-1">{value}</p>
    <p className="text-xs text-muted-foreground">{sub}</p>
  </div>
);

const ChartPanel = ({ title, children }) => (
  <div className="bg-white border border-border/40 rounded-2xl p-5">
    <h3 className="font-serif text-sm text-obsidian mb-4">{title}</h3>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-obsidian text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-amber-300">{p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('sale') ? formatEur(p.value) : p.value}</p>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/dashboard/analytics');
        setData(res.data);
      } catch (e) {
        console.error('Dashboard load failed', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredRecent = useMemo(() => {
    if (!data?.recent_products) return [];
    const q = search.toLowerCase().trim();
    if (!q) return data.recent_products;
    return data.recent_products.filter(
      (r) =>
        r.title?.toLowerCase().includes(q) ||
        r.category?.toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <BarChart3 size={48} className="mx-auto text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">Unable to load dashboard data.</p>
      </div>
    );
  }

  const topCat = data.category_data?.[0]?.name || 'N/A';
  const monthlyGrowth = data.monthly_data?.length >= 2
    ? (() => {
        const last = data.monthly_data[data.monthly_data.length - 1]?.products || 0;
        const prev = data.monthly_data[data.monthly_data.length - 2]?.products || 0;
        return prev > 0 ? `+${Math.round(((last - prev) / prev) * 100)}%` : '+0%';
      })()
    : 'N/A';

  // Format month labels for charts
  const chartMonthly = (data.monthly_data || []).map(m => ({
    ...m,
    label: (() => {
      const [y, mo] = m.month.split('-');
      const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${names[parseInt(mo)] || mo} ${y.slice(2)}`;
    })(),
  }));

  return (
    <div data-testid="dashboard-page">
      <SEOHead title="Dashboard" noindex />

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-2xl md:text-3xl text-obsidian mb-1" data-testid="dashboard-title">
          Business Dashboard
        </h1>
        <p className="text-sm text-muted-foreground">
          {user?.role === 'admin' ? 'Platform-wide analytics' : 'Your product performance'}
        </p>
      </div>

      {/* Revenue Overview Panel (Admin) */}
      {data.membership && (
        <div className="bg-white border border-border/40 rounded-2xl p-5 mb-6" data-testid="revenue-panel">
          <h3 className="font-serif text-sm text-obsidian mb-4">Platform Revenue</h3>

          {/* Revenue Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="border border-amber-200/50 rounded-xl p-4 bg-gradient-to-br from-amber-50/50 to-white" data-testid="membership-revenue">
              <div className="flex items-center gap-2 mb-2">
                <Crown size={16} className="text-amber-600" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Membership Revenue</span>
              </div>
              <p className="font-serif text-2xl text-amber-700">{formatEur(data.membership.mrr)}<span className="text-sm text-muted-foreground font-sans">/month</span></p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {data.membership.creator_members || data.membership.active_members} Creator ({'\u20AC'}29)
                {data.membership.advanced_members > 0 && ` + ${data.membership.advanced_members} Advanced (\u20AC49)`}
              </p>
            </div>

            <div className="border border-blue-200/50 rounded-xl p-4 bg-gradient-to-br from-blue-50/30 to-white" data-testid="product-revenue">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingBag size={16} className="text-blue-600" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Product Revenue</span>
              </div>
              <p className="font-serif text-2xl text-blue-700">{formatEur(data.total_revenue)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{data.total_orders} orders across {data.total_products} products</p>
            </div>

            <div className="border border-emerald-200/50 rounded-xl p-4 bg-gradient-to-br from-emerald-50/30 to-white" data-testid="platform-revenue">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={16} className="text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Platform Revenue</span>
              </div>
              <p className="font-serif text-2xl text-emerald-700">{formatEur(data.platform_revenue)}<span className="text-sm text-muted-foreground font-sans">/month</span></p>
              <p className="text-[10px] text-muted-foreground mt-1">Memberships + product sales</p>
            </div>
          </div>

          {/* Membership Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="membership-stats">
            {[
              { label: 'Active Members', val: data.membership.active_members, icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50', sub: `${'\u20AC'}29/mo each` },
              { label: 'Trial Users', val: data.membership.trial_users, icon: Clock, color: 'text-amber-600 bg-amber-50', sub: '30-day free trial' },
              { label: 'Expired', val: data.membership.expired_members, icon: Users, color: 'text-red-600 bg-red-50', sub: 'Paused access' },
              { label: 'Stripe Confirmed', val: data.membership.stripe_confirmed_payments, icon: CreditCard, color: 'text-violet-600 bg-violet-50', sub: `${formatEur(data.membership.stripe_confirmed_revenue)} total` },
              { label: 'Last 30 Days', val: data.membership.recent_payments_30d, icon: TrendingUp, color: 'text-sky-600 bg-sky-50', sub: `${formatEur(data.membership.recent_revenue_30d)} revenue` },
            ].map(s => (
              <div key={s.label} className={`${s.color} rounded-xl p-3`} data-testid={`mstat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <s.icon size={14} className="mb-1.5" />
                <p className="text-xl font-serif font-bold">{s.val}</p>
                <p className="text-[10px] uppercase tracking-wider">{s.label}</p>
                <p className="text-[9px] opacity-70 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Recent Stripe Payments */}
          {data.membership.recent_subscribers?.length > 0 && (
            <div className="mt-5 border-t border-border/30 pt-4">
              <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-medium">Recent Stripe-Confirmed Subscriptions</h4>
              <div className="space-y-2" data-testid="recent-subscribers">
                {data.membership.recent_subscribers.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-border/20 last:border-0">
                    <div className="flex items-center gap-2">
                      <CreditCard size={12} className="text-violet-500" />
                      <span className="text-muted-foreground">{s.email || s.user_id?.slice(0, 8)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-[9px] capitalize rounded-full">{s.plan}</Badge>
                      <span className="font-medium text-emerald-700">{formatEur(s.amount)}</span>
                      <span className="text-muted-foreground text-[10px]">{s.date?.slice(0, 10)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stat Cards (non-admin or fallback) */}
      {!data.membership && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-grid">
          <StatCard icon={DollarSign} title="Total Revenue" value={formatEur(data.total_revenue)} sub="Across all products" accent="bg-emerald-50" />
          <StatCard icon={ShoppingBag} title="Total Orders" value={data.total_orders} sub="All recorded orders" />
          <StatCard icon={Package} title="Products" value={data.total_products} sub="Published products" accent="bg-blue-50" />
          <StatCard icon={TrendingUp} title="Top Category" value={topCat} sub="Highest revenue" accent="bg-rose-50" />
        </div>
      )}

      {data.membership && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard icon={ShoppingBag} title="Total Orders" value={data.total_orders} sub="All recorded orders" />
          <StatCard icon={Package} title="Products" value={data.total_products} sub="Published products" accent="bg-blue-50" />
          <StatCard icon={Users} title="Total Users" value={data.user_stats?.total || 0} sub={`${data.user_stats?.active || 0} active, ${data.user_stats?.trial || 0} trial`} accent="bg-purple-50" />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <ChartPanel title="Revenue Over Time">
          <div className="h-[260px]" data-testid="chart-revenue">
            {chartMonthly.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartMonthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `\u20AC${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="sales" name="Product Sales" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} />
                  {data.membership && <Line type="monotone" dataKey="membership_revenue" name="Membership Sales" stroke="#d97706" strokeWidth={2.5} dot={{ r: 4, fill: '#d97706' }} />}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
            )}
          </div>
        </ChartPanel>

        <ChartPanel title="Category Distribution">
          <div className="h-[260px]" data-testid="chart-categories">
            {data.category_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.category_data}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {data.category_data.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
            )}
          </div>
        </ChartPanel>
      </div>

      {/* Top Products Bar Chart */}
      <ChartPanel title="Top Products by Revenue">
        <div className="h-[280px]" data-testid="chart-top-products">
          {data.top_products?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_products} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `\u20AC${v}`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#64748b' }} width={140} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="sales" name="Revenue" fill="#d97706" radius={[0, 6, 6, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No products yet</div>
          )}
        </div>
      </ChartPanel>

      {/* Data Table */}
      <div className="bg-white border border-border/40 rounded-2xl p-5 mt-6">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h3 className="font-serif text-sm text-obsidian">Recent Products</h3>
          <div className="relative w-full max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-full text-xs h-9 border-border/50"
              data-testid="dashboard-search"
            />
          </div>
        </div>

        <div className="overflow-x-auto" data-testid="dashboard-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/40">
                <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2.5 px-3">Product</th>
                <th className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2.5 px-3">Category</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2.5 px-3">Price</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2.5 px-3">Orders</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2.5 px-3">Revenue</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium py-2.5 px-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecent.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No products found</td></tr>
              ) : filteredRecent.map((row) => (
                <tr key={row.id} className="border-b border-border/20 hover:bg-amber-50/30 transition-colors">
                  <td className="py-2.5 px-3">
                    <Link to={`/products/${row.id}`} className="text-xs font-medium text-obsidian hover:text-amber-700 transition-colors truncate block max-w-[200px]">
                      {row.title}
                    </Link>
                  </td>
                  <td className="py-2.5 px-3">
                    <Badge variant="outline" className="text-[10px] capitalize rounded-full border-border/50">{row.category}</Badge>
                  </td>
                  <td className="py-2.5 px-3 text-right text-xs text-obsidian">{'\u20AC'}{row.price?.toFixed(2)}</td>
                  <td className="py-2.5 px-3 text-right text-xs text-obsidian">{row.purchases}</td>
                  <td className="py-2.5 px-3 text-right text-xs font-medium text-amber-700">{formatEur(row.revenue)}</td>
                  <td className="py-2.5 px-3 text-right text-[10px] text-muted-foreground">{row.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
